import type { ComponentProps, ReactNode } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion/accordion';
import { cn } from '@/utils/cn';

type AccordionItemType = {
  value: string;
  trigger: ReactNode;
  content: ReactNode;
  triggerClassName?: string;
  contentRootClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
};

type CustomAccordionProps = {
  items: AccordionItemType[];
  type?: 'single' | 'multiple';
  collapsible?: boolean;
  className?: string;
  defaultValue?: string | string[];
  value?: string | string[];
  contentRootClassName?: string;
  contentClassName?: string;
  triggerClassName?: string;
  itemClassName?: string;
  iconName?: string;
  onValueChange?: (value: string | string[]) => void;
  contentBaseClassName?: string;
  triggerBaseClassName?: string;
  rootProps?: Omit<
    ComponentProps<typeof Accordion>,
    'type' | 'defaultValue' | 'value' | 'collapsible' | 'onValueChange'
  > & {
    [key: `data-${string}`]: string | number | boolean | undefined;
  };
};

export function CustomAccordion({
  items,
  type = 'single',
  collapsible = true,
  triggerClassName,
  itemClassName,
  className,
  contentRootClassName,
  contentClassName,
  contentBaseClassName = 'mt-4 px-4',
  defaultValue,
  value,
  iconClosed,
  iconOpened,
  onValueChange,
  rootProps,
  triggerBaseClassName = 'bg-main-800 text-white py-3 px-4 hover:outline-0 rounded-[0.75rem]',
}: CustomAccordionProps & {
  iconClosed?: ReactNode;
  iconOpened?: ReactNode;
}) {
  const { className: rootClassName, ...rootRestProps } = rootProps ?? {};
  const baseProps = {
    ...rootRestProps,
    className: cn('w-full', className, rootClassName),
    onValueChange,
  };

  return type === 'multiple'
    ? (
        <Accordion
          type="multiple"
          defaultValue={defaultValue as string[]}
          value={value as string[] | undefined}
          {...baseProps}
        >
          {items.map(item => (
            <AccordionItem
              key={item.value}
              value={item.value}
              className={cn('border-0', itemClassName, item.itemClassName)}
            >
              <AccordionTrigger
                className={cn(
                  triggerBaseClassName,
                  triggerClassName,
                  item.triggerClassName,
                )}
                iconClosed={iconClosed}
                iconOpened={iconOpened}
              >
                {item.trigger}
              </AccordionTrigger>
              <AccordionContent
                rootClassName={cn(contentRootClassName, item.contentRootClassName)}
                className={cn(contentBaseClassName, contentClassName, item.contentClassName)}
              >
                {item.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )
    : (
        <Accordion
          type="single"
          defaultValue={defaultValue as string}
          value={value as string | undefined}
          collapsible={collapsible}
          {...baseProps}
        >
          {items.map(item => (
            <AccordionItem
              key={item.value}
              value={item.value}
              className={cn('border-0', itemClassName, item.itemClassName)}
            >
              <AccordionTrigger
                className={cn(
                  triggerBaseClassName,
                  triggerClassName,
                  item.triggerClassName,
                )}
                iconClosed={iconClosed}
                iconOpened={iconOpened}
              >
                {item.trigger}
              </AccordionTrigger>
              <AccordionContent
                rootClassName={cn(contentRootClassName, item.contentRootClassName)}
                className={cn(contentBaseClassName, contentClassName, item.contentClassName)}
              >
                {item.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      );
}
